import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Sidebar rail chrome: fixed-width full-height column on the rail surface,
 * with a pinned header, a scrolling middle and a pinned footer.
 *
 *   <vx-sidebar [collapsed]="collapsed()">
 *     <vx-sidebar-header [compact]="collapsed()">…</vx-sidebar-header>
 *     <vx-sidebar-nav>…</vx-sidebar-nav>
 *     <vx-sidebar-footer>…cards…</vx-sidebar-footer>
 *   </vx-sidebar>
 *
 * Slot order in the consumer's template is irrelevant — the shell places
 * header/footer itself; everything else lands in the scrolling middle.
 *
 * `collapsed` only narrows this rail. Children that change shape take their
 * own `compact` input — deliberately not ambient state, so cards and nav
 * items stay usable (and testable) outside a sidebar.
 *
 * Domain-free: it knows nothing about nav entries, users or orgs. The
 * assembled product sidebar (brand mark, routes, permissions, store data)
 * belongs in the app layout layer, not here.
 *
 * Height comes from the parent (`h-full`) — mount it in a sized container.
 * Regions own their horizontal padding so full-bleed children (the active
 * nav indicator) can reach the rail edge.
 */
@Component({
  selector: 'vx-sidebar',
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'classes()',
  },
})
export class VxSidebarComponent {
  readonly collapsed = input(false);

  protected readonly classes = computed(
    () =>
      `flex h-full shrink-0 flex-col bg-zinc-975 pt-2 pb-3 font-brand ${
        this.collapsed() ? 'w-17' : 'w-69'
      }`,
  );
}
